<template>
  <div class="app-container">
    <div style="margin-bottom: 16px; display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
      <el-select v-model="courseTypeCode" filterable clearable placeholder="选择课程类型 code（如 AMC/KANGAROO/GESP）" style="width: 260px;">
        <el-option v-for="ct in courseTypes" :key="ct.code" :label="ct.name + ' (' + ct.code + ')'" :value="ct.code" />
      </el-select>

      <el-select v-model="subjectCode" filterable clearable placeholder="选择级别/子科目（如 AMC8/AMC10/AMC12）" style="width: 220px;">
        <el-option v-for="item in subjectOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select v-model="roleId" placeholder="选择用户类型" style="width: 200px;">
        <el-option v-for="r in roleOptions" :key="r.id" :label="r.label" :value="r.id" />
      </el-select>

      <el-select v-model="tierCode" placeholder="选择档位" style="width: 160px;" :disabled="roleId !== 1">
        <el-option :value="'FREE'" label="普通" />
        <el-option :value="'VIP'" label="会员" />
      </el-select>

      <el-button type="primary" @click="load">加载</el-button>
      <el-button type="success" :disabled="!roleId" @click="save">保存</el-button>
      <el-button :disabled="!roleId" @click="selectAll">全选</el-button>
      <el-button @click="resetSelection" :disabled="!roleId">清空勾选</el-button>

      <el-button type="primary" plain @click="openCreateDialog">新增功能</el-button>

      <span style="color:#909399; font-size:12px;">默认全关：只有勾选并保存的功能才可用</span>
    </div>

    <el-card>
      <div slot="header" style="display:flex; justify-content:space-between; align-items:center;">
        <span>功能列表</span>
        <el-input v-model="keyword" placeholder="搜索功能" size="small" style="width: 260px;" clearable />
      </div>

      <el-table :data="filteredFeatures" border fit>
        <el-table-column label="启用" width="80" align="center">
          <template slot-scope="scope">
            <el-checkbox v-model="enabledMap[scope.row.featureCode]" :disabled="scope.row.status !== 1" />
          </template>
        </el-table-column>
        <el-table-column prop="featureCode" label="featureCode" min-width="220" />
        <el-table-column prop="featureName" label="名称" min-width="180" />
        <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
        <el-table-column label="非会员限制" width="120" align="center">
          <template slot-scope="scope">
            <span v-if="parseLimitConfig(scope.row.limitConfig).freeLimit > 0" class="limit-tag limit-tag-warn">前 {{ parseLimitConfig(scope.row.limitConfig).freeLimit }} 项</span>
            <span v-else class="limit-tag limit-tag-ok">不限</span>
          </template>
        </el-table-column>
        <el-table-column label="会员限制" width="120" align="center">
          <template slot-scope="scope">
            <span v-if="parseLimitConfig(scope.row.limitConfig).vipLimit > 0" class="limit-tag limit-tag-info">前 {{ parseLimitConfig(scope.row.limitConfig).vipLimit }} 项</span>
            <span v-else class="limit-tag limit-tag-ok">不限</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.status === 1">启用</span>
            <span v-else style="color:#f56c6c;">禁用</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="160" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button type="text" size="small" style="color:#f56c6c;" @click="onDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :visible.sync="dialogVisible" :title="dialogMode === 'create' ? '新增功能' : '编辑功能'" width="520px">
      <el-form ref="featureForm" :model="featureForm" label-width="90px">
        <el-form-item label="编码" prop="featureCode" :rules="[{ required: true, message: '请输入功能编码', trigger: 'blur' }]">
          <el-input v-model="featureForm.featureCode" :disabled="dialogMode === 'edit'" />
        </el-form-item>
        <el-form-item label="名称" prop="featureName" :rules="[{ required: true, message: '请输入功能名称', trigger: 'blur' }]">
          <el-input v-model="featureForm.featureName" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="featureForm.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="featureForm.status" style="width: 140px;">
            <el-option :value="1" label="启用" />
            <el-option :value="0" label="禁用" />
          </el-select>
        </el-form-item>

        <el-divider content-position="left">使用范围限制</el-divider>
        <div class="limit-config-hint">配置会员与非会员可访问的内容范围。0 或留空表示不限制。</div>

        <el-form-item label="限制说明">
          <el-input v-model="featureForm.limitLabel" placeholder="如：可查看知识点数 / 可查看小节数 / 可查看年份数" />
        </el-form-item>
        <el-form-item label="非会员限制">
          <div style="display:flex;align-items:center;gap:8px;">
            <el-input-number v-model="featureForm.freeLimit" :min="0" :max="9999" :step="1" controls-position="right" style="width:160px;" />
            <span class="limit-unit">{{ featureForm.limitLabel || '项' }}</span>
            <el-tag v-if="featureForm.freeLimit > 0" type="warning" size="mini">受限</el-tag>
            <el-tag v-else type="success" size="mini">不限</el-tag>
          </div>
        </el-form-item>
        <el-form-item label="会员限制">
          <div style="display:flex;align-items:center;gap:8px;">
            <el-input-number v-model="featureForm.vipLimit" :min="0" :max="9999" :step="1" controls-position="right" style="width:160px;" />
            <span class="limit-unit">{{ featureForm.limitLabel || '项' }}</span>
            <el-tag v-if="featureForm.vipLimit > 0" type="info" size="mini">受限</el-tag>
            <el-tag v-else type="success" size="mini">不限</el-tag>
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="onSubmitFeature">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseCategories } from '@/api/course'
import { listFeatures, listFeaturePolicies, replaceFeaturePolicies, upsertFeature, deleteFeature } from '@/api/feature'

export default {
  name: 'UserFeatureManagement',
  data() {
    return {
      courseTypes: [],
      courseTypeCode: '',
      subjectCode: '',
      roleId: 1,
      tierCode: 'FREE',
      roleOptions: [
        { id: 1, label: '学生' }
      ],
      features: [],
      enabledMap: {},
      keyword: '',

      dialogVisible: false,
      dialogMode: 'create',
      featureForm: {
        id: null,
        featureCode: '',
        featureName: '',
        description: '',
        status: 1,
        freeLimit: 0,
        vipLimit: 0,
        limitLabel: ''
      }
    }
  },
  computed: {
    subjectOptions() {
      const code = String(this.courseTypeCode || '').trim().toUpperCase()
      if (code === 'AMC') {
        return [
          { label: 'AMC8', value: 'AMC8' },
          { label: 'AMC10', value: 'AMC10' },
          { label: 'AMC12', value: 'AMC12' }
        ]
      }
      if (code === 'KANGAROO') {
        return [
          { label: 'KANGAROO LEVEL1', value: 'KANGAROO LEVEL1' },
          { label: 'KANGAROO LEVEL2', value: 'KANGAROO LEVEL2' },
          { label: 'KANGAROO LEVEL3', value: 'KANGAROO LEVEL3' },
          { label: 'KANGAROO LEVEL4', value: 'KANGAROO LEVEL4' },
          { label: 'KANGAROO LEVEL5', value: 'KANGAROO LEVEL5' },
          { label: 'KANGAROO LEVEL6', value: 'KANGAROO LEVEL6' }
        ]
      }
      if (code === 'GESP') {
        return [
          { label: 'CCF-GESP LEVEL1', value: 'CCF-GESP LEVEL1' },
          { label: 'CCF-GESP LEVEL2', value: 'CCF-GESP LEVEL2' },
          { label: 'CCF-GESP LEVEL3', value: 'CCF-GESP LEVEL3' },
          { label: 'CCF-GESP LEVEL4', value: 'CCF-GESP LEVEL4' },
          { label: 'CCF-GESP LEVEL5', value: 'CCF-GESP LEVEL5' },
          { label: 'CCF-GESP LEVEL6', value: 'CCF-GESP LEVEL6' },
          { label: 'CCF-GESP LEVEL7', value: 'CCF-GESP LEVEL7' },
          { label: 'CCF-GESP LEVEL8', value: 'CCF-GESP LEVEL8' }
        ]
      }
      return []
    },
    filteredFeatures() {
      const kw = String(this.keyword || '').trim().toLowerCase()
      const ct = String(this.courseTypeCode || '').trim().toUpperCase()
      const prefix = ct ? `${ct}_` : ''
      const all = Array.isArray(this.features) ? this.features : []
      const arr = prefix ? all.filter(f => String(f.featureCode || '').toUpperCase().startsWith(prefix)) : all
      if (!kw) return arr
      return arr.filter(f => {
        const code = String(f.featureCode || '').toLowerCase()
        const name = String(f.featureName || '').toLowerCase()
        const desc = String(f.description || '').toLowerCase()
        return code.includes(kw) || name.includes(kw) || desc.includes(kw)
      })
    }
  },
  async created() {
    await this.loadCourseTypes()
    await this.loadFeatures()
    await this.load()
  },
  watch: {
    courseTypeCode() {
      const validValues = this.subjectOptions.map(item => item.value)
      if (!validValues.includes(this.subjectCode)) {
        this.subjectCode = validValues[0] || ''
      }
      this.load()
    },
    subjectCode() {
      this.load()
    },
    tierCode() {
      this.load()
    }
  },
  methods: {
    async loadCourseTypes() {
      try {
        const res = await getCourseCategories()
        const list = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
        this.courseTypes = list.map(it => ({
          name: it.name || it.code || '',
          code: String(it.code || '').trim().toUpperCase()
        })).filter(it => it.code)
        if (!this.courseTypeCode && this.courseTypes.length > 0) {
          this.courseTypeCode = this.courseTypes[0].code
        }
        if (!this.subjectCode && this.subjectOptions.length > 0) {
          this.subjectCode = this.subjectOptions[0].value
        }
      } catch (e) {
        console.error(e)
      }
    },
    buildPolicyPayload(enabledFeatureCodes) {
      return {
        roleId: this.roleId,
        courseTypeCode: this.courseTypeCode || null,
        subjectCode: this.subjectCode || null,
        tierCode: this.roleId === 1 ? this.tierCode : 'FREE',
        enabledFeatureCodes
      }
    },
    async loadFeatures() {
      const res = await listFeatures({ enabledOnly: false })
      this.features = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
      const map = {}
      this.features.forEach(f => { map[f.featureCode] = false })
      this.enabledMap = map
    },
    parseLimitConfig(raw) {
      if (!raw) return { freeLimit: 0, vipLimit: 0, limitLabel: '' }
      try {
        const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
        return {
          freeLimit: Number(obj.freeLimit) || 0,
          vipLimit: Number(obj.vipLimit) || 0,
          limitLabel: obj.limitLabel || ''
        }
      } catch (e) {
        return { freeLimit: 0, vipLimit: 0, limitLabel: '' }
      }
    },
    buildLimitConfig(form) {
      const fl = Number(form.freeLimit) || 0
      const vl = Number(form.vipLimit) || 0
      const label = String(form.limitLabel || '').trim()
      if (fl <= 0 && vl <= 0 && !label) return null
      return JSON.stringify({ freeLimit: fl, vipLimit: vl, limitLabel: label })
    },
    openCreateDialog() {
      this.dialogMode = 'create'
      this.featureForm = {
        id: null,
        featureCode: '',
        featureName: '',
        description: '',
        status: 1,
        freeLimit: 0,
        vipLimit: 0,
        limitLabel: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.featureForm) this.$refs.featureForm.clearValidate()
      })
    },
    openEditDialog(row) {
      if (!row) return
      this.dialogMode = 'edit'
      const lc = this.parseLimitConfig(row.limitConfig)
      this.featureForm = {
        id: row.id,
        featureCode: row.featureCode,
        featureName: row.featureName,
        description: row.description,
        status: row.status,
        freeLimit: lc.freeLimit,
        vipLimit: lc.vipLimit,
        limitLabel: lc.limitLabel
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.featureForm) this.$refs.featureForm.clearValidate()
      })
    },
    async onSubmitFeature() {
      if (this.$refs.featureForm) {
        const ok = await this.$refs.featureForm.validate().catch(() => false)
        if (!ok) return
      }
      const payload = {
        id: this.featureForm.id,
        featureCode: String(this.featureForm.featureCode || '').trim(),
        featureName: String(this.featureForm.featureName || '').trim(),
        description: this.featureForm.description,
        status: this.featureForm.status,
        limitConfig: this.buildLimitConfig(this.featureForm)
      }
      const res = await upsertFeature(payload)
      if (res && res.code === 1) {
        if (this.dialogMode === 'create' && this.roleId && payload.featureCode) {
          const enabled = Object.keys(this.enabledMap).filter(code => this.enabledMap[code])
          if (!enabled.includes(payload.featureCode)) {
            enabled.push(payload.featureCode)
          }
          const policyRes = await replaceFeaturePolicies(this.buildPolicyPayload(enabled))
          if (!policyRes || policyRes.code !== 1) {
            this.$message.error((policyRes && policyRes.msg) || '功能已保存，但策略同步失败')
            await this.loadFeatures()
            await this.load()
            return
          }
        }
        this.$message.success('保存成功')
        this.dialogVisible = false
        await this.loadFeatures()
        await this.load()
      } else {
        this.$message.error((res && res.msg) || '保存失败')
      }
    },
    onDelete(row) {
      if (!row || !row.id) return
      this.$confirm('确认删除该功能？删除后策略中也无法再配置该编码。', '提示', { type: 'warning' }).then(async() => {
        const res = await deleteFeature(row.id)
        if (res && res.code === 1) {
          this.$message.success('删除成功')
          await this.loadFeatures()
          await this.load()
        } else {
          this.$message.error((res && res.msg) || '删除失败')
        }
      }).catch(() => {})
    },
    async load() {
      if (!this.roleId) return
      const res = await listFeaturePolicies({ roleId: this.roleId, courseTypeCode: this.courseTypeCode || undefined, subjectCode: this.subjectCode || undefined, tierCode: this.roleId === 1 ? this.tierCode : 'FREE' })
      const list = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
      const map = { ...this.enabledMap }
      Object.keys(map).forEach(k => { map[k] = false })
      list.forEach(p => {
        if (p && p.featureCode) map[p.featureCode] = Number(p.enabled) === 1
      })
      this.enabledMap = map
    },
    resetSelection() {
      const map = { ...this.enabledMap }
      Object.keys(map).forEach(k => { map[k] = false })
      this.enabledMap = map
    },
    selectAll() {
      const map = { ...this.enabledMap }
      const rows = Array.isArray(this.filteredFeatures) ? this.filteredFeatures : []
      rows.forEach(row => {
        if (row && row.featureCode && Number(row.status) === 1) {
          map[row.featureCode] = true
        }
      })
      this.enabledMap = map
    },
    async save() {
      if (!this.roleId) return
      const enabled = Object.keys(this.enabledMap).filter(code => this.enabledMap[code])
      const payload = this.buildPolicyPayload(enabled)
      const res = await replaceFeaturePolicies(payload)
      if (res && res.code === 1) {
        this.$message.success('保存成功')
      } else {
        this.$message.error((res && res.msg) || '保存失败')
      }
    }
  }
}
</script>

<style scoped>
.limit-config-hint {
  font-size: 12px;
  color: #909399;
  margin-bottom: 12px;
  padding: 0 10px;
  line-height: 1.6;
}

.limit-unit {
  font-size: 13px;
  color: #606266;
  min-width: 80px;
}

.limit-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
}

.limit-tag-ok {
  color: #67c23a;
  background: #f0f9eb;
}

.limit-tag-warn {
  color: #e6a23c;
  background: #fdf6ec;
}

.limit-tag-info {
  color: #409eff;
  background: #ecf5ff;
}
</style>
